import { useState } from 'react';
import { API_BASE_URL } from '../api/coworkApi';

/**
 * One avatar component for every agent surface (chat list, message thread,
 * call sidebar, empty states). Renders the image when it loads and falls back
 * to a gradient tile with the agent's emoji — or initials if no emoji — when
 * the URL is missing OR the image fails to load (onError).
 *
 * Previously each surface inlined a raw <img> with no onError handler, so a
 * 404'd avatar showed the browser's broken-image glyph / alt-text fragments
 * (e.g. "loar", "vkk") — the single biggest trust-killer on the Chats screen.
 */
function initials(name?: string): string {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return name.slice(0, 2).toUpperCase();
}

export function AgentAvatar({
  name,
  avatarUrl,
  emoji,
  gradientFrom,
  gradientTo,
  accentColor,
  className = '',
  textClassName = 'text-sm',
}: {
  name?: string;
  avatarUrl?: string;
  emoji?: string;
  gradientFrom?: string;
  gradientTo?: string;
  accentColor?: string;
  /** sizing + shape + margins — applied to both the image and the fallback */
  className?: string;
  /** glyph size for the fallback tile */
  textClassName?: string;
}) {
  const [errored, setErrored] = useState(false);
  const src = avatarUrl
    ? (avatarUrl.startsWith('http') ? avatarUrl : `${API_BASE_URL}${avatarUrl}`)
    : null;

  if (src && !errored) {
    return (
      <img
        src={src}
        alt={name || ''}
        onError={() => setErrored(true)}
        className={`object-cover ${className}`}
      />
    );
  }

  const from = gradientFrom || accentColor || '#be43ff';
  const to = gradientTo || accentColor || '#be43ff';
  return (
    <div
      className={`flex items-center justify-center ${textClassName} ${className}`}
      style={{
        background: `linear-gradient(135deg, ${from}20, ${to}10)`,
        border: accentColor ? `1.5px solid ${accentColor}25` : undefined,
        fontWeight: 600,
      }}
    >
      {emoji || initials(name)}
    </div>
  );
}
